/**
 * Apple-style scroll entrance: a section's heading group (eyebrow / heading / description) and cards rise + fade in as they scroll into the viewport, playing only once.
 * Modeled on the effect when first scrolling down the apple.com iPhone Pro page.
 *
 * Usage: add data-soulog-reveal to the elements to animate.
 * The shared section head snippets/soulog-block-head.liquid and the shared feature row snippets/soulog-feature-row.liquid
 * have it by default; sections with their own headings / cards add it manually.
 *
 * Triggering is per **individual element**, not per group: elements entering the viewport together in the same callback (cards in the same row, adjacent
 * heading and description) are staggered by STAGGER_MS in DOM order; vertically stacked cards (card lists on mobile) each play only when scrolled to.
 * If triggered per group, the whole column would finish as soon as the group's top entered the screen, and the cards below would already be static when scrolled to — effectively no effect.
 *
 * State lives in the data-sr-state attribute (styles in soulog-text-reveal.css):
 *   pending → initially hidden (set only by this script, so content is always visible if JS fails)
 *   in      → transitioning
 *   done    → transition finished; transition removed so it no longer interferes with the element's own styles/transitions
 *
 * Three safeguards:
 *   1. Elements already in the viewport at init go straight to done instead of hide-then-show — above-the-fold content doesn't flash or delay LCP.
 *   2. System "reduce motion", Animations turned off in the theme editor, or no IntersectionObserver in the browser: do nothing.
 *   3. After shopify:section:load in the theme editor, re-initialize on the new DOM.
 */
(() => {
	if (window.__soulogTextReveal) return;
	window.__soulogTextReveal = true;

	const SELECTOR = "[data-soulog-reveal]";
	const STAGGER_MS = 120;
	// How deep into the viewport before triggering: the element's top edge crosses the line 12% up from the viewport bottom
	const ROOT_MARGIN = "0px 0px -12% 0px";
	// At init, an element whose top edge is above this line counts as "already above the fold" and is shown directly
	const VISIBLE_LINE = 0.88;

	const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	if (reduceMotion || !("IntersectionObserver" in window)) return;
	if (document.body && document.body.classList.contains("animations-false")) return;

	const finish = (el) => {
		el.setAttribute("data-sr-state", "done");
		el.style.removeProperty("--sr-delay");
	};

	const play = (el, delay) => {
		el.style.setProperty("--sr-delay", `${delay}ms`);
		el.setAttribute("data-sr-state", "in");
		const onEnd = (e) => {
			// Only the end of translate counts (it's longer than opacity); transitionend events bubbling up from children are ignored
			if (e.target !== el || e.propertyName !== "translate") return;
			el.removeEventListener("transitionend", onEnd);
			finish(el);
		};
		el.addEventListener("transitionend", onEnd);
	};

	const byDomOrder = (a, b) => (a.compareDocumentPosition && a.compareDocumentPosition(b) & 4 /* FOLLOWING */ ? -1 : 1);

	const io = new IntersectionObserver(
		(entries) => {
			const hits = entries.filter((e) => e.isIntersecting).map((e) => e.target);
			hits.sort(byDomOrder).forEach((el, i) => {
				io.unobserve(el);
				play(el, i * STAGGER_MS);
			});
		},
		{ rootMargin: ROOT_MARGIN, threshold: 0 },
	);

	const init = (root) => {
		const line = window.innerHeight * VISIBLE_LINE;
		root.querySelectorAll(SELECTOR).forEach((el) => {
			if (el.hasAttribute("data-sr-state")) return;
			const rect = el.getBoundingClientRect();
			const onScreen = rect.bottom > 0 && rect.top < line && (rect.width > 0 || rect.height > 0);
			if (onScreen) {
				finish(el);
				return;
			}
			el.setAttribute("data-sr-state", "pending");
			io.observe(el);
		});
	};

	init(document);
	document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
